import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { useState } from "react";

function providerLabel(provider?: string) {
  if (provider === "aio") return "AIO";
  if (provider === "gemini") return "Gemini";
  if (provider === "chatgpt") return "ChatGPT";
  return "全部";
}

export default function AiEvidence() {
  const { loading } = useAuth();
  const [provider, setProvider] = useState<"all" | "aio" | "gemini" | "chatgpt">("all");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");

  const { data, isLoading } = trpc.dashboard.getAiEvidenceList.useQuery({
    provider: provider === "all" ? undefined : provider,
    status: status === "all" ? undefined : status,
    page,
    pageSize: 20,
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8">載入中...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">AI 檢查證據留痕</h1>
            <p className="text-muted-foreground">每次檢查都保留原始回應、標準化結果與可信度</p>
          </div>
          <div className="flex items-center gap-2">
            <select className="h-9 rounded-md border bg-background px-2 text-sm" value={provider} onChange={(e) => { setProvider(e.target.value as any); setPage(1); }}>
              <option value="all">全部來源</option>
              <option value="aio">AIO</option>
              <option value="gemini">Gemini</option>
              <option value="chatgpt">ChatGPT</option>
            </select>
            <select className="h-9 rounded-md border bg-background px-2 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="all">全部狀態</option>
              <option value="success_with_signal">有品牌訊號</option>
              <option value="success_no_signal">無品牌訊號</option>
              <option value="success_no_overview">無摘要</option>
              <option value="error">錯誤</option>
            </select>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>證據列表</CardTitle>
            <CardDescription>來源：{providerLabel(provider)} · 共 {data?.total || 0} 筆</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">載入中...</div>
            ) : (data?.items || []).length ? (
              <div className="space-y-3">
                {(data?.items || []).map((row: any) => (
                  <div key={row.id} className="rounded-md border p-3 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium">{row.keyword || `Keyword #${row.keywordId}`}</div>
                      <div className="text-muted-foreground">{new Date(row.finishedAt).toLocaleString("zh-TW")}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {providerLabel(row.provider)} · {row.status} · confidence {row.confidenceScore}
                    </div>
                    <div className="text-xs">
                      品牌提及：{row.brandMentioned ? "是" : "否"} · 品牌連結：{row.brandLinked ? "是" : "否"}
                    </div>
                    {row.errorMessage ? (
                      <div className="text-xs text-red-600">{row.errorMessage}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">暫無資料</div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((v) => Math.max(1, v - 1))}>上一頁</Button>
          <div className="text-sm text-muted-foreground">第 {page} 頁</div>
          <Button variant="outline" disabled={(data?.items || []).length < (data?.pageSize || 20)} onClick={() => setPage((v) => v + 1)}>下一頁</Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
